import { InfiniteData, useQueryClient } from "@tanstack/react-query";
import { shallowEqual } from "react-redux";
import { racehorse360 } from "@tsg/1st-grpc-web-rh360";
import { deepObjectUpdater } from "utils/deepObjectUpdater";
import { useAppSelector } from "../redux/useAppSelector";

interface IUseSetQueryDataReturnType {
  updateUpcomingRaceEntries: (
    entryIds: string[],
    patch: racehorse360.IUpcomingRaceEntry
  ) => Promise<void>;
  updateWorkoutRequests: (
    entryIds: string[],
    patch: racehorse360.IWorkoutRequest
  ) => Promise<void>;
  filterUpcomingRaceEntries: (
    entryIds: string[],
    refetchQueryKey: string
  ) => Promise<void>;
  filterWorkoutRequests: (
    workoutRequestIds: string[],
    requestKey: string
  ) => Promise<void>;
  refetchUpcomingRaceEntriesIfOrderedByFlag: () => Promise<void>;
  refetchWorkoutRequestsIfOrderedByFlag: () => Promise<void>;
}

type TListRequestKey = (string | object)[];

const FLAG_ORDER_FIELDS = new Set(["isFlagged", "flag.priority"]);

const checkIsOrderedByFlag = (requestKey: TListRequestKey): boolean => {
  const request = requestKey?.[1] as { getOptions?: { orderBy?: string[] } };
  const orderRules = request?.getOptions?.orderBy ?? [];

  return orderRules.some(rule => FLAG_ORDER_FIELDS.has(rule.split(" ")[0]));
};

interface IUseSetListHorsesQueryDataReturnType {
  updateListHorses: (
    horseIds: string[],
    patch: racehorse360.IHorse,
    queryKey: (string | object)[]
  ) => void;
}

type TListHorsesResponse =
  | InfiniteData<racehorse360.ListHorsesResponse>
  | racehorse360.ListHorsesResponse;

const isInfiniteHorseListGuard = (
  data:
    | racehorse360.ListHorsesResponse
    | InfiniteData<racehorse360.ListHorsesResponse>
): data is InfiniteData<racehorse360.ListHorsesResponse> => {
  return (
    (data as InfiniteData<racehorse360.ListHorsesResponse>).pages !== undefined
  );
};

export const useSetQueryData = (): IUseSetQueryDataReturnType => {
  const queryClient = useQueryClient();

  const { upcomingRaceListRequestKey, workoutRequestListRequestKey } =
    useAppSelector(state => {
      return {
        upcomingRaceListRequestKey:
          state.preRaceScreeningPage.upcomingRaceListRequestKey,
        workoutRequestListRequestKey:
          state.vetWorkoutsPage.workoutRequestListRequestKey
      };
    }, shallowEqual);

  const updateUpcomingRaceEntries = async (
    entryIds: string[],
    patch: racehorse360.IUpcomingRaceEntry
  ): Promise<void> => {
    await queryClient.cancelQueries({
      queryKey: upcomingRaceListRequestKey,
      exact: true
    });

    queryClient.setQueryData(
      upcomingRaceListRequestKey,
      (oldData: InfiniteData<racehorse360.ListUpcomingRaceEntriesResponse>) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map(page => {
            return new racehorse360.ListUpcomingRaceEntriesResponse({
              ...page,
              upcomingRaceEntries: page.upcomingRaceEntries.map(entry => {
                if (entryIds.includes(entry.id)) {
                  return new racehorse360.UpcomingRaceEntry(
                    deepObjectUpdater(entry, patch)
                  );
                }

                return entry;
              })
            });
          })
        };
      }
    );
  };

  const updateWorkoutRequests = async (
    entryIds: string[],
    patch: racehorse360.IWorkoutRequest
  ): Promise<void> => {
    await queryClient.cancelQueries({
      queryKey: workoutRequestListRequestKey,
      exact: true
    });

    queryClient.setQueryData(
      workoutRequestListRequestKey,
      (oldData: InfiniteData<racehorse360.ListWorkoutRequestsResponse>) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map(page => {
            return new racehorse360.ListWorkoutRequestsResponse({
              ...page,
              workoutRequests: page.workoutRequests.map(entry => {
                if (entryIds.includes(entry.id)) {
                  return new racehorse360.WorkoutRequest(
                    deepObjectUpdater(entry, patch)
                  );
                }

                return entry;
              })
            });
          })
        };
      }
    );
  };

  const filterUpcomingRaceEntries = async (
    entryIds: string[],
    refetchQueryKey: string
  ): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: [refetchQueryKey],
      refetchType: "active"
    });

    queryClient.setQueryData(
      upcomingRaceListRequestKey,
      (oldData: InfiniteData<racehorse360.ListUpcomingRaceEntriesResponse>) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map(page => {
            return new racehorse360.ListUpcomingRaceEntriesResponse({
              ...page,
              upcomingRaceEntries: page.upcomingRaceEntries.filter(
                entry => !entryIds.includes(entry.id)
              )
            });
          })
        };
      }
    );
  };

  const filterWorkoutRequests = async (
    workoutRequestIds: string[],
    refetchQueryKey: string
  ): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: [refetchQueryKey],
      refetchType: "active"
    });

    queryClient.setQueryData(
      workoutRequestListRequestKey,
      (oldData: InfiniteData<racehorse360.ListWorkoutRequestsResponse>) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map(page => {
            return new racehorse360.ListWorkoutRequestsResponse({
              ...page,
              workoutRequests: page.workoutRequests.filter(
                entry => !workoutRequestIds.includes(entry.id)
              )
            });
          })
        };
      }
    );
  };

  const refetchListIfOrderedByFlag = async (
    requestKey: TListRequestKey
  ): Promise<void> => {
    if (!checkIsOrderedByFlag(requestKey)) return;

    await queryClient.invalidateQueries({
      queryKey: requestKey,
      exact: true
    });
  };

  const refetchUpcomingRaceEntriesIfOrderedByFlag = (): Promise<void> =>
    refetchListIfOrderedByFlag(upcomingRaceListRequestKey);

  const refetchWorkoutRequestsIfOrderedByFlag = (): Promise<void> =>
    refetchListIfOrderedByFlag(workoutRequestListRequestKey);

  return {
    updateUpcomingRaceEntries,
    updateWorkoutRequests,
    filterUpcomingRaceEntries,
    filterWorkoutRequests,
    refetchUpcomingRaceEntriesIfOrderedByFlag,
    refetchWorkoutRequestsIfOrderedByFlag
  };
};

export const useSetListHorsesQueryData =
  (): IUseSetListHorsesQueryDataReturnType => {
    const queryClient = useQueryClient();

    const updateListHorses = (
      horseIds: string[],
      patch: racehorse360.IHorse,
      queryKey: (string | object)[]
    ): void => {
      queryClient.setQueryData(
        queryKey,
        (oldData: TListHorsesResponse): TListHorsesResponse => {
          if (!oldData) return oldData;

          if (isInfiniteHorseListGuard(oldData)) {
            return {
              ...oldData,
              pages: oldData.pages.map(page => {
                return new racehorse360.ListHorsesResponse({
                  ...page,
                  horses: page.horses.map(horse => {
                    if (horseIds.includes(horse.id)) {
                      return new racehorse360.Horse(
                        deepObjectUpdater(horse, patch)
                      );
                    }

                    return horse;
                  })
                });
              })
            };
          }

          return new racehorse360.ListHorsesResponse({
            ...oldData,
            horses: oldData.horses.map(horse => {
              if (horseIds.includes(horse.id)) {
                return new racehorse360.Horse(deepObjectUpdater(horse, patch));
              }

              return horse;
            })
          });
        }
      );
    };

    return {
      updateListHorses
    };
  };
